import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BiologicalLayout from '@/components/layout/BiologicalLayout';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import {
  Moon, Heart, Footprints, ChevronRight, Loader2,
  AlertTriangle, Shield, Activity, Sun, Zap, Camera, Upload
} from 'lucide-react';
import WhoopConnectCard from '@/components/wearables/WhoopConnectCard';

// Biological state styling
const STATE_CONFIG = {
  disrupted_recovery: { label: 'Recovery Disrupted', color: '#8A4343', bg: 'rgba(138,67,67,0.08)', icon: AlertTriangle },
  autonomic_suppression: { label: 'Nervous System Under Load', color: '#8A6B43', bg: 'rgba(138,107,67,0.08)', icon: Heart },
  compensation_mode: { label: 'Body Adapting', color: '#7A7045', bg: 'rgba(122,112,69,0.08)', icon: Activity },
  energy_conservation: { label: 'Reserves Need Protection', color: '#8A6B43', bg: 'rgba(138,107,67,0.08)', icon: Shield },
  restored: { label: 'Balanced Recovery', color: '#4E7A6B', bg: 'rgba(78,122,107,0.08)', icon: Sun },
  resilient: { label: 'Strong Biological Day', color: '#3D7A4E', bg: 'rgba(61,122,78,0.08)', icon: Zap },
};

export default function WearablesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState('enter');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [response, setResponse] = useState(null);
  const [todaySignal, setTodaySignal] = useState(null);
  const [loadingToday, setLoadingToday] = useState(true);

  // Form state — simple, minimal fields
  const [sleepHours, setSleepHours] = useState('');
  const [deepHours, setDeepHours] = useState('');
  const [awakenings, setAwakenings] = useState('');
  const [wakeTime, setWakeTime] = useState('');
  const [hrv, setHrv] = useState('');
  const [restingHr, setRestingHr] = useState('');
  const [activeMinutes, setActiveMinutes] = useState('');
  const [steps, setSteps] = useState('');
  const [screenshotExtracting, setScreenshotExtracting] = useState(false);

  const subscriberId = user?.role === 'caregiver' ? user?.linked_subscriber_id : user?.id;

  // Load today's signal on mount
  useEffect(() => {
    if (!subscriberId) return;
    const fetchToday = async () => {
      try {
        const res = await axios.get(`${API}/wearable/today/${subscriberId}`);
        if (res.data?.id) {
          setTodaySignal(res.data);
          setStep('result');
          setResponse(res.data);
        }
      } catch {
        // No signal today — show entry form
      } finally {
        setLoadingToday(false);
      }
    };
    fetchToday();
  }, [subscriberId]);

  const hasAnyInput = sleepHours || deepHours || awakenings || wakeTime || hrv || restingHr || activeMinutes || steps;

  const handleSubmit = async () => {
    if (!hasAnyInput) {
      toast.error('Enter at least one signal to continue');
      return;
    }
    setIsSubmitting(true);
    try {
      const body = { date: new Date().toISOString().split('T')[0] };
      if (sleepHours || deepHours || awakenings || wakeTime) {
        body.sleep = {};
        if (sleepHours) body.sleep.total_hours = parseFloat(sleepHours);
        if (deepHours) body.sleep.deep_hours = parseFloat(deepHours);
        if (awakenings) body.sleep.awakenings = parseInt(awakenings);
        if (wakeTime) body.sleep.wake_time = wakeTime;
      }
      if (hrv || restingHr) {
        body.recovery = {};
        if (hrv) body.recovery.hrv_ms = parseFloat(hrv);
        if (restingHr) body.recovery.resting_hr_bpm = parseInt(restingHr);
      }
      if (activeMinutes || steps) {
        body.exertion = {};
        if (activeMinutes) body.exertion.active_minutes = parseInt(activeMinutes);
        if (steps) body.exertion.steps = parseInt(steps);
      }

      const res = await axios.post(`${API}/wearable/manual-entry/${subscriberId}`, body);
      setResponse(res.data);
      setStep('result');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not process signals');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewEntry = () => {
    setStep('enter');
    setResponse(null);
    setTodaySignal(null);
    setSleepHours(''); setDeepHours(''); setAwakenings(''); setWakeTime('');
    setHrv(''); setRestingHr(''); setActiveMinutes(''); setSteps('');
  };

  const handleScreenshotUpload = async (file) => {
    if (!file || !subscriberId) return;
    setScreenshotExtracting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/wearable/screenshot-extract/${subscriberId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      const d = res.data;
      // Auto-fill form fields from extraction
      if (d.sleep?.total_hours) setSleepHours(String(d.sleep.total_hours));
      if (d.sleep?.deep_hours) setDeepHours(String(d.sleep.deep_hours));
      if (d.sleep?.awakenings) setAwakenings(String(d.sleep.awakenings));
      if (d.recovery?.hrv_ms) setHrv(String(d.recovery.hrv_ms));
      if (d.recovery?.resting_hr_bpm) setRestingHr(String(d.recovery.resting_hr_bpm));
      if (d.exertion?.active_minutes) setActiveMinutes(String(d.exertion.active_minutes));
      if (d.exertion?.steps) setSteps(String(d.exertion.steps));
      toast.success('Screenshot data extracted — review and submit');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not extract data from screenshot');
    } finally {
      setScreenshotExtracting(false);
    }
  };

  if (loadingToday) {
    return (
      <BiologicalLayout>
        <div className="max-w-2xl mx-auto px-6 py-14 flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-6 h-6 text-bio-ghost animate-spin" />
        </div>
      </BiologicalLayout>
    );
  }

  return (
    <BiologicalLayout>
      <div className="max-w-2xl mx-auto px-6 sm:px-8 py-10 sm:py-14" data-testid="wearables-page">

        {/* Title */}
        <div className="mb-12">
          <p className="text-[11px] uppercase tracking-[0.3em] text-bio-faint mb-3 font-['Work_Sans']" data-testid="wearables-label">
            Daily Signals
          </p>
          <h1 className="text-3xl sm:text-4xl font-['Outfit'] font-light text-bio-primary leading-tight" data-testid="wearables-title">
            How is your body today?
          </h1>
        </div>

        {/* === ENTRY FORM === */}
        {step === 'enter' && (
          <div className="space-y-10">

            <p className="text-sm text-bio-secondary leading-relaxed font-['Work_Sans'] font-light max-w-xl" data-testid="wearables-intro">
              Share what you know about last night and yesterday. Enter whatever you have &mdash; even one number helps your care system respond.
            </p>

            {/* WHOOP Connect Card — reusable provider integration */}
            <WhoopConnectCard subscriberId={user?.id} onChanged={() => {}} />

            {/* Screenshot Upload Option */}
            <div className="rounded-xl border border-[#EBE8E3] p-4 flex items-center gap-4" data-testid="screenshot-upload-section">
              <div className="w-10 h-10 rounded-full bg-[#8C6B55]/10 flex items-center justify-center flex-shrink-0">
                <Camera className="w-5 h-5 text-[#8C6B55]" strokeWidth={1.25} />
              </div>
              <div className="flex-1">
                <p className="text-sm text-[#2C2A29] font-['Work_Sans'] font-medium">Upload wearable screenshot</p>
                <p className="text-[11px] text-[#8A8681] font-['Work_Sans']">Auto-extract sleep, HRV, steps from your watch app</p>
              </div>
              <input type="file" id="wearable-screenshot" className="hidden" accept="image/*"
                onChange={e => { const f = e.target.files?.[0]; if (f) handleScreenshotUpload(f); }}
                data-testid="screenshot-file-input" />
              <button
                onClick={() => document.getElementById('wearable-screenshot')?.click()}
                disabled={screenshotExtracting}
                className="px-4 py-2 rounded-xl text-xs font-['Work_Sans'] font-medium transition-all flex items-center gap-1.5"
                style={{ backgroundColor: '#8C6B55', color: '#FBFaf9', opacity: screenshotExtracting ? 0.5 : 1 }}
                data-testid="screenshot-upload-btn"
              >
                {screenshotExtracting ? <><Loader2 className="w-3 h-3 animate-spin" /> Extracting...</> : <><Upload className="w-3 h-3" /> Upload</>}
              </button>
            </div>

            {/* Sleep & Rest */}
            <section data-testid="sleep-section">
              <div className="flex items-center gap-2.5 mb-5">
                <Moon className="w-4 h-4 text-bio-accent" strokeWidth={1.5} />
                <p className="text-[11px] uppercase tracking-[0.25em] text-bio-subtle font-['Work_Sans'] font-medium">
                  Sleep & Rest
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <FieldInput label="Total sleep" unit="hours" value={sleepHours} onChange={setSleepHours} type="number" step="0.5" min="0" max="24" testId="input-sleep-hours" placeholder="e.g. 6.5" />
                <FieldInput label="Deep sleep" unit="hours" value={deepHours} onChange={setDeepHours} type="number" step="0.5" min="0" max="12" testId="input-deep-hours" placeholder="if known" />
                <FieldInput label="Times woken" unit="" value={awakenings} onChange={setAwakenings} type="number" step="1" min="0" max="20" testId="input-awakenings" placeholder="e.g. 3" />
                <FieldInput label="Wake time" unit="" value={wakeTime} onChange={setWakeTime} type="time" testId="input-wake-time" placeholder="" />
              </div>
            </section>

            {/* Recovery */}
            <section data-testid="recovery-section">
              <div className="flex items-center gap-2.5 mb-3">
                <Heart className="w-4 h-4 text-bio-accent" strokeWidth={1.5} />
                <p className="text-[11px] uppercase tracking-[0.25em] text-bio-subtle font-['Work_Sans'] font-medium">
                  Recovery Signals
                </p>
              </div>
              <p className="text-[12px] text-bio-ghost font-['Work_Sans'] mb-4 leading-relaxed">
                If your wearable shows these, enter them here. If not, skip this section.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <FieldInput label="Heart rate variability" unit="ms" value={hrv} onChange={setHrv} type="number" step="1" min="0" max="300" testId="input-hrv" placeholder="e.g. 28" />
                <FieldInput label="Resting heart rate" unit="bpm" value={restingHr} onChange={setRestingHr} type="number" step="1" min="30" max="150" testId="input-resting-hr" placeholder="e.g. 68" />
              </div>
            </section>

            {/* Movement */}
            <section data-testid="movement-section">
              <div className="flex items-center gap-2.5 mb-3">
                <Footprints className="w-4 h-4 text-bio-accent" strokeWidth={1.5} />
                <p className="text-[11px] uppercase tracking-[0.25em] text-bio-subtle font-['Work_Sans'] font-medium">
                  Yesterday's Movement
                </p>
              </div>
              <p className="text-[12px] text-bio-ghost font-['Work_Sans'] mb-4 leading-relaxed">
                Approximate is fine. This helps understand your body's exertion level.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <FieldInput label="Active minutes" unit="min" value={activeMinutes} onChange={setActiveMinutes} type="number" step="1" min="0" max="1440" testId="input-active-min" placeholder="e.g. 20" />
                <FieldInput label="Steps" unit="" value={steps} onChange={setSteps} type="number" step="100" min="0" max="100000" testId="input-steps" placeholder="e.g. 4000" />
              </div>
            </section>

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !hasAnyInput}
              className="w-full py-3.5 rounded-xl text-sm font-['Work_Sans'] font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-30"
              style={{ backgroundColor: '#8C6B55', color: '#FBFaf9' }}
              data-testid="submit-signals-btn"
            >
              {isSubmitting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Reading signals...</>
              ) : (
                <>Read my signals <ChevronRight className="w-4 h-4" /></>
              )}
            </button>
          </div>
        )}

        {/* === BIOLOGICAL RESPONSE === */}
        {step === 'result' && response && <BiologicalResponse data={response} onNewEntry={handleNewEntry} navigate={navigate} />}

        <div className="pt-8 pb-4">
          <div className="w-8 h-px bg-bio-border mx-auto" />
        </div>
      </div>
    </BiologicalLayout>
  );
}


// ── Field Input Component ─────────────────────────────────
function FieldInput({ label, unit, value, onChange, type, step, min, max, testId, placeholder }) {
  return (
    <div>
      <label className="text-[11px] text-bio-subtle mb-1.5 block font-['Work_Sans']">
        {label} {unit && <span className="text-bio-ghost">({unit})</span>}
      </label>
      <Input
        type={type}
        step={step}
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-bio-surface-raised/50 border-bio-border text-bio-primary text-sm font-['Work_Sans'] placeholder:text-bio-ghost/50"
        data-testid={testId}
      />
    </div>
  );
}


// ── Biological Response Display ───────────────────────────
function BiologicalResponse({ data, onNewEntry, navigate }) {
  const interp = data?.interpretation || {};
  const narrative = interp.biological_narrative || {};
  const state = interp.biological_state || 'restored';
  const readiness = interp.readiness || {};
  const trajectory = interp.trajectory || {};
  const quality = data?.data_quality || {};
  const hints = data?.coaching_hints || {};
  const adjustments = data?.guidance_adjustments || {};

  const config = STATE_CONFIG[state] || STATE_CONFIG.restored;
  const StateIcon = config.icon;

  return (
    <div className="space-y-8 animate-in fade-in duration-500" data-testid="biological-response">

      {/* State Header */}
      <div className="rounded-2xl p-6 sm:p-8" style={{ backgroundColor: config.bg }} data-testid="state-header">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: `${config.color}15` }}>
            <StateIcon className="w-5 h-5" style={{ color: config.color }} strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] font-['Work_Sans'] mb-1.5" style={{ color: config.color }}>
              {config.label}
            </p>
            <p className="text-bio-primary text-[15px] leading-relaxed font-['Work_Sans'] font-light" data-testid="narrative-body-showing">
              {narrative.what_body_is_showing}
            </p>
          </div>
        </div>
      </div>

      {/* Contributing Factors */}
      {narrative.contributing_factors && narrative.contributing_factors.length > 0 && narrative.contributing_factors[0] !== 'no specific signals to highlight' && (
        <section data-testid="contributing-factors">
          <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-3 font-['Work_Sans']">
            What may be contributing
          </p>
          <div className="space-y-2">
            {narrative.contributing_factors.map((factor, i) => (
              <p key={i} className="text-sm text-bio-secondary font-['Work_Sans'] font-light leading-relaxed pl-3" style={{ borderLeft: `2px solid ${config.color}30` }}>
                {factor.charAt(0).toUpperCase() + factor.slice(1)}
              </p>
            ))}
          </div>
        </section>
      )}

      {/* How to move through the day */}
      <section className="rounded-2xl p-6" style={{ backgroundColor: 'rgba(140,107,85,0.04)', border: '1px solid rgba(140,107,85,0.1)' }} data-testid="day-guidance">
        <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-3 font-['Work_Sans']">
          How to move through today
        </p>
        <p className="text-[15px] text-bio-primary leading-[1.8] font-['Work_Sans'] font-light" data-testid="narrative-guidance">
          {narrative.how_to_move_through_day}
        </p>
      </section>

      {/* Coaching through the day */}
      {Object.keys(hints).length > 0 && (
        <section data-testid="coaching-hints-section">
          <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-4 font-['Work_Sans']">
            Through your day
          </p>
          <div className="space-y-3">
            {Object.entries(hints).map(([block, hint]) => (
              <div key={block} className="flex items-start gap-3" data-testid={`hint-${block}`}>
                <span className="text-[10px] uppercase tracking-[0.2em] text-bio-ghost font-['Work_Sans'] min-w-[60px] pt-0.5 text-right">
                  {block}
                </span>
                <p className="text-sm text-bio-secondary font-['Work_Sans'] font-light leading-relaxed">
                  {hint.hint}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Adjustments */}
      {Object.keys(adjustments).length > 1 && (
        <section data-testid="adjustments-section">
          <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-4 font-['Work_Sans']">
            Adjustments for today
          </p>
          <div className="space-y-3">
            {Object.entries(adjustments).map(([key, adj]) => (
              <div key={key} className="rounded-xl p-4" style={{ backgroundColor: 'rgba(78,122,107,0.04)' }}>
                <p className="text-sm text-bio-primary font-['Work_Sans'] font-light leading-relaxed mb-1">
                  {adj.adjustment}
                </p>
                <p className="text-[11px] text-bio-ghost font-['Work_Sans'] leading-relaxed">
                  {adj.reason}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Data confidence note */}
      {quality.confidence_level && quality.confidence_level !== 'high' && (
        <p className="text-[11px] text-bio-ghost font-['Work_Sans'] leading-relaxed italic" data-testid="confidence-note">
          {quality.confidence_level === 'low'
            ? 'Based on limited signals. More data helps your system respond with greater precision.'
            : 'Based on partial signals. The more you share, the clearer the picture becomes.'}
        </p>
      )}

      {/* Trajectory note */}
      {trajectory.direction && trajectory.direction !== 'insufficient_data' && trajectory.direction !== 'stable_strong' && (
        <div className="rounded-xl p-4" style={{ backgroundColor: 'rgba(140,107,85,0.04)', border: '1px solid rgba(140,107,85,0.08)' }} data-testid="trajectory-note">
          <p className="text-[11px] text-bio-subtle font-['Work_Sans'] leading-relaxed">
            {trajectory.direction === 'declining' && 'Your signals have been trending lower over the past few days. Prioritizing rest may help your body find its balance again.'}
            {trajectory.direction === 'improving' && 'Your signals show an upward trend recently. What you have been doing appears to be helping.'}
            {trajectory.direction === 'unstable' && 'Your signals have been fluctuating. Your body may be adjusting to changes in routine or environment.'}
            {trajectory.direction === 'stable_low' && 'Your signals have been consistently lower than usual. Consider whether something in your routine needs attention.'}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3 pt-2">
        <button
          onClick={onNewEntry}
          className="py-2.5 px-5 rounded-xl text-sm font-['Work_Sans'] border border-bio-border text-bio-body hover:bg-bio-surface-raised/50 transition-colors"
          data-testid="update-signals-btn"
        >
          Update signals
        </button>
        <button
          onClick={() => navigate('/dashboard')}
          className="py-2.5 px-5 rounded-xl text-sm font-['Work_Sans'] font-medium flex items-center gap-2"
          style={{ backgroundColor: '#8C6B55', color: '#FBFaf9' }}
          data-testid="go-dashboard-btn"
        >
          Dashboard <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
